import { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

export interface HistoryColumn<T> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  render: (row: T) => ReactNode;
}

interface HistoryTableProps<T> {
  columns: HistoryColumn<T>[];
  rows: T[];
  isLoading?: boolean;
  emptyMessage?: string;
  rowKey: (row: T) => string;
}

export function HistoryTable<T>({
  columns,
  rows,
  isLoading,
  emptyMessage = 'No history found.',
  rowKey,
}: HistoryTableProps<T>) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600 dark:text-warm-sand">
        <Loader2 className="w-5 h-5 animate-spin text-amber-450 mr-2" />
        Loading…
      </div>
    );
  }

  if (!rows.length) {
    return <p className="text-gray-600 dark:text-warm-sand py-8 text-center">{emptyMessage}</p>;
  }

  const alignClass = (align?: 'left' | 'right' | 'center') => {
    if (align === 'right') return 'text-right';
    if (align === 'center') return 'text-center';
    return 'text-left';
  };

  return (
    <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="table w-full min-w-[600px]">
          <thead className="bg-gray-100 dark:bg-charcoal-700/50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase ${alignClass(col.align)}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-charcoal-700">
            {rows.map((row) => (
              <tr key={rowKey(row)} className="hover:bg-gray-50 dark:hover:bg-charcoal-700/30">
                {columns.map((col) => (
                  <td key={col.key} className={`px-4 py-3 ${alignClass(col.align)}`}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
